import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  BookOpen, 
  Plus, 
  Eye, 
  Star, 
  Edit, 
  Trash2,
  Loader2,
  Clock,
  BarChart3,
  PenLine,
  Save,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AuthorDashboard() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, views: 0, avgRating: 0 });
  const [penName, setPenName] = useState('');
  const [penNameSaving, setPenNameSaving] = useState(false);
  const [penNameJustSaved, setPenNameJustSaved] = useState(false);

  useEffect(() => {
    setPenName(user?.pen_name || '');
  }, [user?.pen_name]);

  const savePenName = async () => {
    setPenNameSaving(true);
    try {
      const res = await api.patch('/auth/me/pen-name', { pen_name: penName.trim() });
      const { stories_updated, articles_updated } = res.data || {};
      toast.success(
        penName.trim()
          ? `Nickname aplicado (${stories_updated || 0} contos, ${articles_updated || 0} artigos atualizados)`
          : 'Nickname removido — voltaste ao teu nome real'
      );
      setPenNameJustSaved(true);
      setTimeout(() => setPenNameJustSaved(false), 2500);
      if (refresh) await refresh();
      // Reload stories so the new author_name shows in the list.
      const res2 = await api.get('/my-stories');
      setStories(res2.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setPenNameSaving(false);
    }
  };

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await api.get('/my-stories');
        setStories(res.data);
        
        // Calculate stats
        const totalViews = res.data.reduce((sum, s) => sum + s.views, 0);
        const avgRating = res.data.length > 0 
          ? res.data.reduce((sum, s) => sum + s.rating, 0) / res.data.length 
          : 0;
        
        setStats({
          total: res.data.length,
          views: totalViews,
          avgRating: avgRating.toFixed(1)
        });
      } catch (error) {
        console.error('Error fetching stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, []);

  const handleDelete = async (storyId) => {
    try {
      await api.delete(`/stories/${storyId}`);
      setStories(stories.filter(s => s.id !== storyId));
      toast.success('Conto eliminado com sucesso');
    } catch (error) {
      toast.error('Erro ao eliminar conto');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Meus Contos</h1>
                <p className="text-muted-foreground">Gerencie suas publicações</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/author/analytics')}
                data-testid="author-analytics-btn"
                className="border-border hover:border-ruby/50"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
              <Button
                onClick={() => navigate('/write')}
                data-testid="author-new-story"
                className="bg-ruby hover:bg-ruby-light rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Conto
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Total de Contos</p>
              <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Visualizações</p>
              <p className="text-2xl font-bold text-foreground">{stats.views.toLocaleString()}</p>
            </div>
            <div className="bg-surface rounded-xl border border-border p-4">
              <p className="text-muted-foreground text-sm">Avaliação Média</p>
              <p className="text-2xl font-bold text-foreground flex items-center gap-1">
                <Star className="w-5 h-5 text-gold fill-gold" />
                {stats.avgRating}
              </p>
            </div>
          </div>

          {/* Nome de autor (pen name) */}
          <div
            className="bg-surface rounded-xl border border-border p-5 mb-8"
            data-testid="pen-name-card"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-ruby/10 flex items-center justify-center shrink-0">
                <PenLine className="w-5 h-5 text-ruby" />
              </div>
              <div className="flex-1">
                <h2 className="font-brand text-lg font-semibold text-foreground">
                  Nome de autor público
                </h2>
                <p className="text-sm text-muted-foreground">
                  Escolhe um nickname para assinar os teus contos. Substitui o teu nome real em toda a parte pública da Intimate.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1 min-w-0">
                <Label htmlFor="pen-name" className="text-xs text-muted-foreground">
                  O teu nickname (máx. 60 caracteres · deixa vazio para usar &quot;{user?.name || 'o teu nome'}&quot;)
                </Label>
                <Input
                  id="pen-name"
                  value={penName}
                  onChange={(e) => setPenName(e.target.value.slice(0, 60))}
                  placeholder="Ex.: Lua Vermelha, Luísa S., Vermelho..."
                  className="bg-background border-border mt-1"
                  data-testid="author-pen-name-input"
                />
              </div>
              <Button
                onClick={savePenName}
                disabled={penNameSaving || penName === (user?.pen_name || '')}
                data-testid="author-pen-name-save"
                className="bg-ruby hover:bg-ruby-light shrink-0"
              >
                {penNameSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : penNameJustSaved ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {penNameJustSaved ? 'Guardado' : 'Guardar'}
              </Button>
            </div>
            {user?.pen_name && (
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                <PenLine className="w-3 h-3 text-purple-300" />
                Neste momento, os teus contos aparecem publicamente como{' '}
                <strong className="text-foreground">{user.pen_name}</strong>.
              </p>
            )}
          </div>

          {/* Stories List */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          ) : stories.length > 0 ? (
            <div className="space-y-4">
              {stories.map(story => (
                <div 
                  key={story.id}
                  className="bg-surface rounded-xl border border-border p-4 flex gap-4"
                >
                  {/* Cover */}
                  <img
                    src={story.cover_image}
                    alt={story.title}
                    className="w-20 h-28 object-cover rounded-lg flex-shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-brand text-lg font-semibold text-foreground truncate">
                          {story.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {story.synopsis}
                        </p>
                      </div>
                      {story.is_premium && (
                        <span className="premium-badge flex-shrink-0">Premium</span>
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {story.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        {story.rating.toFixed(1)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {story.reading_time} min
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/edit/${story.id}`)}
                        data-testid={`edit-${story.id}`}
                        className="border-border"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/story/${story.slug || story.id}`)}
                        className="border-border"
                      >
                        Ver
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-surface border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminar conto?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Esta ação não pode ser desfeita. O conto será permanentemente eliminado.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(story.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-muted mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Ainda não publicou nenhum conto
              </h2>
              <p className="text-muted-foreground mb-6">
                Compartilhe tuas histórias com a comunidade
              </p>
              <Button
                onClick={() => navigate('/write')}
                data-testid="author-first-story"
                className="bg-ruby hover:bg-ruby-light rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Escrever meu primeiro conto
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
